import { useEffect, useRef } from 'react';
import { debugLog } from '../../../../utils/debugLog';
import {
  AI_PROVIDER_MODELS,
  normalizeProviderType,
  DEFAULT_FORM_DATA,
} from '../../../../config/constants';

/**
 * useTemplateSync Hook
 * Syncs template provider with available adapters after both are loaded
 */
export const useTemplateSync = (
  providers,
  loadedTemplateProvider,
  loadedTemplateModel,
  isEditMode,
  formData,
  setFormData,
  templateId
) => {
  const hasSyncedProvider = useRef(false);
  const previousProvidersCount = useRef(0);

  // Reset sync flag when template changes
  useEffect(() => {
    hasSyncedProvider.current = false;
    previousProvidersCount.current = 0;
  }, [templateId]);

  // Reset sync flag when providers change from empty to non-empty
  // This allows sync to run when providers are loaded after template is loaded
  useEffect(() => {
    if (previousProvidersCount.current === 0 && providers.length > 0) {
      debugLog(
        '🔄 Providers loaded (was empty, now has providers), resetting sync flag'
      );
      hasSyncedProvider.current = false;
    }
    previousProvidersCount.current = providers.length;
  }, [providers.length]);

  useEffect(() => {
    debugLog('useTemplateSync effect:', {
      providersCount: providers.length,
      loadingProviders: providers.length === 0, // Assume loading if no providers
      loadedTemplateProvider,
      loadedTemplateModel,
      isEditMode,
      hasSyncedProvider: hasSyncedProvider.current,
      currentFormProvider: formData.model_config.provider,
      currentFormModel: formData.model_config.model_name,
      providerIds: providers.map(p => p.id),
    });

    // Wait for providers to be loaded before syncing
    // Only sync if:
    // 1. Providers are loaded (providers.length > 0)
    // 2. Template has a provider configured (loadedTemplateProvider)
    // 3. We're in edit mode
    // 4. We haven't synced yet
    if (
      providers.length > 0 &&
      loadedTemplateProvider &&
      isEditMode &&
      !hasSyncedProvider.current
    ) {
      // Try to match by provider type (id, provider, or name)
      const matchingAdapter = providers.find(
        adapter =>
          adapter.id === loadedTemplateProvider ||
          adapter.provider === loadedTemplateProvider ||
          adapter.name === loadedTemplateProvider
      );

      debugLog('Provider matching:', {
        loadedTemplateProvider,
        matchingAdapter: matchingAdapter
          ? {
              id: matchingAdapter.id,
              name: matchingAdapter.name,
              provider: matchingAdapter.provider,
            }
          : null,
        allProviders: providers.map(p => ({
          id: p.id,
          name: p.name,
          provider: p.provider,
        })),
      });

      if (matchingAdapter) {
        const models = matchingAdapter.models || [];
        const modelExists = models.some(
          m =>
            m.id === loadedTemplateModel ||
            m.value === loadedTemplateModel ||
            m.name === loadedTemplateModel
        );

        debugLog('Setting provider/model from template:', {
          matchingAdapterId: matchingAdapter.id,
          loadedTemplateModel,
          modelExists,
          availableModels: models.map(m => ({
            id: m.id,
            value: m.value,
            name: m.name,
          })),
        });

        const selectedModelName = modelExists
          ? loadedTemplateModel
          : models[0]?.value || models[0]?.id || models[0]?.name || '';

        // Get model-specific max_tokens from AI_PROVIDER_MODELS
        const normalizedProvider = normalizeProviderType(matchingAdapter.id);
        const providerModels = AI_PROVIDER_MODELS[normalizedProvider];
        const modelConfig =
          providerModels?.modelConfig?.[selectedModelName] || {};

        setFormData(prev => {
          // Calculate max_tokens inside the callback to access prev
          const maxTokens =
            modelConfig.max_tokens ||
            prev.model_config.max_tokens ||
            DEFAULT_FORM_DATA.model_config.max_tokens;

          return {
            ...prev,
            model_config: {
              ...prev.model_config,
              provider: matchingAdapter.id,
              model_name: selectedModelName,
              // Update max_tokens if model-specific value is available
              max_tokens: maxTokens,
              // Preserve existing values for temperature and frequency_penalty if already set
              temperature:
                prev.model_config.temperature ??
                DEFAULT_FORM_DATA.model_config.temperature,
              frequency_penalty:
                prev.model_config.frequency_penalty ??
                DEFAULT_FORM_DATA.model_config.frequency_penalty,
            },
          };
        });
        hasSyncedProvider.current = true;
      } else {
        debugLog('No matching adapter found for provider:', {
          loadedTemplateProvider,
          availableProviderIds: providers.map(p => p.id),
          availableProviderNames: providers.map(p => p.name),
        });
        // If no matching adapter found, still preserve the loaded values
        // Don't clear them - they might be valid but just not in the current provider list
        // Only set if formData doesn't already have values
        if (!formData.model_config.provider && loadedTemplateProvider) {
          setFormData(prev => ({
            ...prev,
            model_config: {
              ...prev.model_config,
              provider: loadedTemplateProvider, // Keep the original provider value
              model_name:
                loadedTemplateModel || prev.model_config.model_name || '',
            },
          }));
        }
        hasSyncedProvider.current = true; // Mark as synced to prevent repeated attempts
      }
    }
    // For new templates (create mode), auto-select first provider and model
    // Set default values for configuration fields (temperature, max_tokens, etc.)
    else if (
      providers.length > 0 &&
      !isEditMode &&
      (!formData.model_config?.provider ||
        formData.model_config.provider === '') &&
      !hasSyncedProvider.current
    ) {
      const firstAdapter = providers[0];
      const firstModel = firstAdapter.models?.[0];

      if (firstAdapter.id && firstModel) {
        const modelName = firstModel.value || firstModel.id || firstModel.name;
        const normalizedProvider = normalizeProviderType(firstAdapter.id);

        // Get model-specific max_tokens from AI_PROVIDER_MODELS
        const providerModels = AI_PROVIDER_MODELS[normalizedProvider];
        const modelConfig = providerModels?.modelConfig?.[modelName] || {};
        const maxTokens =
          modelConfig.max_tokens || DEFAULT_FORM_DATA.model_config.max_tokens;

        debugLog('Auto-selecting first provider/model for new template:', {
          provider: firstAdapter.id,
          model: modelName,
          maxTokens,
          temperature: DEFAULT_FORM_DATA.model_config.temperature,
          frequencyPenalty: DEFAULT_FORM_DATA.model_config.frequency_penalty,
        });

        setFormData(prev => ({
          ...prev,
          model_config: {
            ...prev.model_config,
            // Auto-select first provider and model
            provider: firstAdapter.id,
            model_name: modelName,
            // Set default values for configuration fields
            temperature:
              prev.model_config.temperature ??
              DEFAULT_FORM_DATA.model_config.temperature,
            max_tokens: maxTokens,
            top_p:
              prev.model_config.top_p ?? DEFAULT_FORM_DATA.model_config.top_p,
            frequency_penalty:
              prev.model_config.frequency_penalty ??
              DEFAULT_FORM_DATA.model_config.frequency_penalty,
            presence_penalty:
              prev.model_config.presence_penalty ??
              DEFAULT_FORM_DATA.model_config.presence_penalty,
          },
        }));
        hasSyncedProvider.current = true; // Mark as synced to prevent repeated attempts
      } else {
        debugLog(
          '⚠️ No models available for first provider, setting defaults only:',
          {
            provider: firstAdapter.id,
            providerName: firstAdapter.name,
            hasModels: !!firstAdapter.models,
            modelsCount: firstAdapter.models?.length || 0,
          }
        );
        // If no models, still set defaults but leave provider/model empty
        setFormData(prev => ({
          ...prev,
          model_config: {
            ...prev.model_config,
            provider: '',
            model_name: '',
            temperature:
              prev.model_config.temperature ??
              DEFAULT_FORM_DATA.model_config.temperature,
            max_tokens:
              prev.model_config.max_tokens ??
              DEFAULT_FORM_DATA.model_config.max_tokens,
            top_p:
              prev.model_config.top_p ?? DEFAULT_FORM_DATA.model_config.top_p,
            frequency_penalty:
              prev.model_config.frequency_penalty ??
              DEFAULT_FORM_DATA.model_config.frequency_penalty,
            presence_penalty:
              prev.model_config.presence_penalty ??
              DEFAULT_FORM_DATA.model_config.presence_penalty,
          },
        }));
        hasSyncedProvider.current = true;
      }
    }
  }, [
    providers,
    providers.length, // Explicitly depend on length to trigger when providers load
    loadedTemplateProvider,
    loadedTemplateModel,
    isEditMode,
    setFormData,
    templateId,
    // Note: Removed formData.model_config.provider from deps to avoid
    // preventing sync when formData already has values set during template load
  ]);

  return { hasSyncedProvider };
};
